/** i18n — dictionaries are sibling JSON (ADR 0009). IIFE keeps a tiny EN fallback. */

import { sdkAssetUrl } from "../assets.js";

export type Lang = "en" | "ru" | "es";

export type Dict = {
  signIn: string;
  connected: string;
  relogin: string;
  create: string;
  restore: string;
  close: string;
  noKey: string;
  saveKey: string;
  saveHint: string;
  copy: string;
  copied: string;
  done: string;
  reveal: string;
  hide: string;
  account: string;
  accountStatus: string;
  accountHint: string;
  logout: string;
  paste: string;
  cont: string;
  qr: string;
  brand: string;
};

/** Short English until `i18n/{lang}.json` loads. */
export const fallback: Dict = {
  signIn: "Sign in with",
  connected: "Connected",
  create: "Create key",
  restore: "Restore key",
  close: "Close",
  noKey: "No key yet.",
  saveKey: "Save key",
  saveHint: "Needed on other devices.",
  copy: "Copy",
  copied: "Copied",
  done: "Done",
  reveal: "Reveal",
  hide: "Hide",
  account: "Device key",
  accountStatus: "Account status",
  accountHint: "Scan to verify identity or establish trust line.",
  logout: "Sign out",
  paste: "Paste or QR.",
  cont: "Continue",
  qr: "QR",
  brand: "d.wtf",
  relogin: "Relogin",
};

const cache = new Map<Lang, Dict>();
const inflight = new Map<Lang, Promise<Dict>>();

export function resolveLang(explicit?: string): Lang {
  const raw = (explicit || (typeof navigator !== "undefined" ? navigator.language : "en") || "en")
    .slice(0, 2)
    .toLowerCase();
  return raw === "ru" || raw === "es" ? raw : "en";
}

export function t(lang?: string): Dict {
  return cache.get(resolveLang(lang)) ?? fallback;
}

export async function loadDict(lang?: string): Promise<Dict> {
  const l = resolveLang(lang);
  const hit = cache.get(l);
  if (hit) return hit;
  const pending = inflight.get(l);
  if (pending) return pending;
  if (typeof fetch !== "function") return fallback;
  const p = (async () => {
    try {
      const res = await fetch(sdkAssetUrl(`i18n/${l}.json`));
      if (!res.ok) return fallback;
      const j = (await res.json()) as Partial<Dict>;
      const d: Dict = { ...fallback, ...j };
      cache.set(l, d);
      return d;
    } catch {
      return fallback;
    } finally {
      inflight.delete(l);
    }
  })();
  inflight.set(l, p);
  return p;
}

export type Brand = "d.wtf" | "an.one" | "neutral" | { label?: string };

export function brandLabel(brand: Brand | undefined, d: Dict): string {
  if (brand === "neutral") return "";
  if (brand === "d.wtf" || brand === "an.one") return brand;
  if (brand && typeof brand === "object") return brand.label ?? "";
  return d.brand;
}
